import { useMemo, useState } from 'react'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button as ChakraButton,
  CircularProgress,
  Flex,
  Link,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ActiveTokenOffer, TokenOfferType } from 'modules/offer/types'
import { NEVER_EXPIRY } from 'modules/order/constants'
import NextLink from 'next/link'
import { expandExponential } from 'utils/numbers'

import { Button } from 'components/Buttons'

const EMPTY_DATA: ActiveTokenOffer[] = [1, 2, 3, 4].map((x) => ({
  _id: x.toString(),
  symbol: x.toString(),
  sellToken: x.toString(),
  sellAmount: x,
  sellTokenSymbol: x.toString(),
  buyToken: x.toString(),
  buyAmount: x,
  buyTokenSymbol: x.toString(),
  price: x,
  priceSymbol: x.toString(),
  priceUSD: x,
  hideUSD: false,
  expiry: x,
}))

interface IActiveTokenOffersTable {
  type: TokenOfferType
  data: ActiveTokenOffer[]
  chainId: number
  selectedToken: string
  loading?: boolean
  loadingPagination?: boolean
}

const ActiveTokenOffersTable = ({
  type,
  data = [],
  chainId,
  selectedToken,
  loading = false,
  loadingPagination,
}: IActiveTokenOffersTable) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'expiry', desc: false },
  ])
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const pagination = useMemo(
    () => ({
      pageIndex, // current page index
      pageSize, // size of pages
      pageCount: Math.ceil(data.length / pageSize), // number of pages
    }),
    [pageIndex, pageSize, data.length]
  )

  const tableData = useMemo(
    () =>
      loading
        ? EMPTY_DATA
        : data.slice(
            pagination.pageSize * pagination.pageIndex,
            pagination.pageSize * (pagination.pageIndex + 1)
          ),
    [loading, data, pagination]
  )

  const columns = useMemo<ColumnDef<ActiveTokenOffer>[]>(
    () => [
      {
        accessorKey: 'symbol',
        header: 'Symbol',
        footer: '',
        cell: (props) =>
          loading ? <Skeleton width="60px" height="12px" /> : props.getValue(),
      },
      {
        accessorKey: 'buyAmount',
        header: "You'll Send",
        footer: '',
        cell: (props) =>
          loading ? (
            <Skeleton width="60px" height="12px" />
          ) : (
            `${props.getValue()} ${props.row.original?.buyTokenSymbol ?? ''}`
          ),
      },
      {
        accessorKey: 'sellAmount',
        header: "You'll Receive",
        footer: '',
        cell: (props) =>
          loading ? (
            <Skeleton width="60px" height="12px" />
          ) : (
            `${props.getValue()} ${props.row.original?.sellTokenSymbol ?? ''}`
          ),
      },
      {
        accessorKey: 'priceUSD',
        header: 'Price Per Token',
        footer: '',
        cell: (props) =>
          loading ? (
            <Skeleton width="60px" height="12px" />
          ) : (
            <Box>
              <Text>
                {expandExponential(props.row.original?.price ?? 0).slice(0, 16)}{' '}
                {props.row.original?.priceSymbol ?? ''}
              </Text>
              {!props.row.original?.hideUSD && Number(props.getValue()) > 0 ? (
                <Text fontSize="sm" color="muted">
                  (${expandExponential(Number(props.getValue())).slice(0, 16)})
                </Text>
              ) : null}
            </Box>
          ),
      },
      {
        accessorKey: 'expiry',
        header: 'Offer Expiry',
        footer: '',
        cell: (props) => {
          if (loading) return <Skeleton width="60px" height="12px" />
          if (props.getValue() === NEVER_EXPIRY) return 'Never'
          let delta = Math.abs(Number(props.getValue()) - Date.now()) / 1000

          // calculate (and subtract) whole days
          const days = Math.floor(delta / 86400)
          delta -= days * 86400

          // calculate (and subtract) whole hours
          const hours = Math.floor(delta / 3600) % 24
          delta -= hours * 3600

          // calculate (and subtract) whole minutes
          const minutes = Math.floor(delta / 60) % 60
          delta -= minutes * 60

          return (
            <Text>
              {days > 0 ? `${days}d ` : ''}
              {hours > 0 ? `${hours}h ` : ''}
              {minutes > 0 ? `${minutes}min` : ''}
            </Text>
          )
        },
      },
      {
        accessorKey: '_id',
        header: '',
        footer: '',
        cell: (props) =>
          loading ? (
            <Skeleton width="60px" height="12px" />
          ) : (
            <NextLink
              href={`/trade/${props.getValue()}?network=${chainId}`}
              passHref
            >
              <Link _hover={{ textDecoration: 'none' }}>
                {type === TokenOfferType.User ? (
                  <Button
                    variant={'outline'}
                    color="highlight"
                    borderColor={'highlight'}
                    fontFamily={'mono'}
                    borderRadius={'xl'}
                    _hover={{
                      bg: 'highlight',
                      color: 'white',
                    }}
                    cursor={'pointer'}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    variant={'outline'}
                    color="highlight"
                    borderColor={'highlight'}
                    fontFamily={'mono'}
                    borderRadius={'xl'}
                    _hover={{
                      bg: 'highlight',
                      color: 'white',
                    }}
                    cursor={'pointer'}
                  >
                    {type}
                  </Button>
                )}
              </Link>
            </NextLink>
          ),
      },
    ],
    [loading, chainId, type]
  )

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Box
      bg="dark"
      borderRadius="xl"
      border="2px solid"
      borderColor="mid"
      display={{ base: 'table', lg: 'inherit' }}
      mx={{ base: 5, sm: 8, md: 0 }}
    >
      <Table variant={'brand'} size={{ base: 'sm', md: 'md' }}>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <Flex
                      alignItems="center"
                      {...{
                        cursor: header.column.getCanSort()
                          ? 'pointer'
                          : 'initial',
                        onClick: () => {
                          const isSorted = header.column.getIsSorted()
                          header.column.toggleSorting(
                            isSorted === false || isSorted === 'desc'
                              ? false
                              : true
                          )
                        },
                      }}
                      userSelect="none"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() === 'asc' ? (
                        <TriangleUpIcon
                          color="white"
                          aria-label="sorted ascending"
                          ml={2}
                        />
                      ) : header.column.getIsSorted() === 'desc' ? (
                        <TriangleDownIcon
                          color="white"
                          aria-label="sorted descending"
                          ml={2}
                        />
                      ) : null}
                    </Flex>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {(table.getRowModel().rows.length > 0 &&
            table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))) || (
            <Tr>
              <Td colSpan={6}>
                {type === TokenOfferType.User ? (
                  <Box textAlign="center" py={8}>
                    <Text color="muted" pb={8}>
                      You have no active orders for {selectedToken} 😅
                    </Text>
                    <NextLink href={'/trade'} passHref>
                      <Link _hover={{ textDecoration: 'none' }}>
                        <Button
                          borderRadius={'xl'}
                          bg={'brand'}
                          size={'xl'}
                          fontFamily={'mono'}
                          boxShadow={'0px 3px 0px rgba(70, 126, 229, 0.6)'}
                          _focus={{ boxShadow: 'none' }}
                          color={'white'}
                          px={6}
                          py={4}
                        >
                          + Create New Buy or Sell Order
                        </Button>
                      </Link>
                    </NextLink>
                  </Box>
                ) : (
                  <Text textAlign="center" color="muted" py={16}>
                    No {type === TokenOfferType.Buy ? 'buy' : 'sell'} offers
                    available for {selectedToken} 😅
                  </Text>
                )}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <Box py={loading ? 7 : 4}>
        {!loading && (
          <Flex
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            visibility={
              table.getRowModel().rows.length > 0 ? 'visible' : 'hidden'
            }
          >
            <Box mr="auto" ml={4} minWidth="30px" />
            <ChakraButton
              size="sm"
              variant="link"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <FiArrowLeft color="#467EE5" />
            </ChakraButton>
            <Flex alignItems="center" px={2}>
              <Text color="muted">
                {`Page ${
                  table.getState().pagination.pageIndex + 1
                } of ${table.getPageCount()}`}
              </Text>
            </Flex>
            <ChakraButton
              size="sm"
              variant="link"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <FiArrowRight color="#467EE5" />
            </ChakraButton>
            <Box ml="auto" mr={4} minWidth="30px" textAlign="end">
              {loadingPagination && (
                <CircularProgress
                  isIndeterminate
                  size="22px"
                  trackColor="gray.600"
                />
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  )
}

export default ActiveTokenOffersTable
